import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { MappedLeadData, CampaignMetadata } from '../../types';
import { formatRevenue } from '../../utils/helpers';
import { getResearchSummarySectionsForTab } from '../../utils/researchSummary';
import { SimpleTabContent } from '../common/Atomic';

interface ISRBriefingTabProps {
  data: MappedLeadData;
  campaign?: CampaignMetadata;
}

interface QuietInsightModel {
  title: string;
  eyebrow: string;
  icon: string;
  body: string;
  bullets?: string[];
}

interface AccountFactModel {
  label: string;
  value: string;
}

interface AccountContextModel extends Omit<QuietInsightModel, 'bullets'> {
  facts: AccountFactModel[];
}

interface RiskRowModel {
  label: string;
  value: string;
  icon: string;
  tone: 'amber' | 'rose' | 'slate';
}

interface ISRBriefingViewModel {
  /** Facts consumed by the identity panel OUTSIDE the briefing (left area). */
  context: {
    timezone: string;
    bestTime?: string;
    latestActivity?: string;
    meetingUrl?: string;
  };
  /** Campaign reference shown on the Campaign tab. */
  campaign: {
    name: string;
    objective: string;
    summary: string;
    niche: string;
    pain: string;
    value: string;
    proof: string;
    outreachChannels: string;
    timing: string;
    missing: string[];
  };
  /** Lead-level ICP angles — top pain, trigger, value prop, proof. The most
   *  call-critical material, straight from the lead's enriched ICP record. */
  angles: Array<{ label: string; body: string; tone: 'pain' | 'trigger' | 'value' | 'proof' }>;
  leadFit: QuietInsightModel;
  whyNow: QuietInsightModel;
  accountContext: AccountContextModel;
  openingAngle: QuietInsightModel;
  conversationGuidance: QuietInsightModel;
  risks: RiskRowModel[];
}

const isPresent = (value: unknown): value is string => (
  typeof value === 'string' &&
  value.trim().length > 0 &&
  !['unknown', 'n/a', 'not available', '#', 'null', 'undefined'].includes(value.trim().toLowerCase())
);

const cleanText = (value: unknown): string => {
  if (!isPresent(value)) return '';
  return value
    .replace(/\r\n/g, '\n')
    .replace(/[#*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const truncateWords = (value: string, maxWords = 42) => {
  const words = cleanText(value).split(' ').filter(Boolean);
  if (words.length <= maxWords) return words.join(' ');
  return `${words.slice(0, maxWords).join(' ')}...`;
};

const parseMaybeJson = (value: unknown): any => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const unique = (items: Array<string | null | undefined>, limit = 5) => {
  const seen = new Set<string>();
  const result: string[] = [];

  items.forEach((item) => {
    const cleaned = cleanText(item || '');
    if (!cleaned) return;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(cleaned);
  });

  return result.slice(0, limit);
};

const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => cleanText(item)).filter(Boolean);
  }

  const cleaned = cleanText(value);
  return cleaned ? [cleaned] : [];
};

const firstPresent = (...values: unknown[]) => {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) return cleaned;
  }
  return '';
};

const textFromAny = (value: unknown): string => {
  if (!value) return '';

  if (Array.isArray(value)) {
    return unique(value.map((item) => textFromAny(item)), 2).join(' ');
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return firstPresent(
      record.summary,
      record.text,
      record.content,
      record.description,
      record.value,
      record.title
    );
  }

  return cleanText(value);
};

const normalizeCampaignKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/\(ai\)/g, '')
    .replace(/[^a-z0-9]+/g, '');

const getCampaignCustomField = (campaign: CampaignMetadata | undefined, fieldNames: string[]): string => {
  if (!campaign) return '';
  const rawCampaign = campaign as CampaignMetadata & Record<string, any>;
  const targets = fieldNames.map(normalizeCampaignKey);
  const containers = [
    rawCampaign.custom_fields,
    rawCampaign.customFields,
    rawCampaign.customfields,
    rawCampaign.custom_fields_data,
  ].filter(Boolean);

  for (const container of containers) {
    if (Array.isArray(container)) {
      for (const field of container) {
        if (!field || typeof field !== 'object') continue;
        const name = firstPresent(field.name, field.key, field.label, field.fieldName);
        if (!name || !targets.includes(normalizeCampaignKey(name))) continue;
        const value = textFromAny(field.value ?? field.text ?? field.content ?? field.answer ?? field.fieldValue);
        if (value) return value;
      }
      continue;
    }

    if (typeof container !== 'object') continue;
    for (const [key, value] of Object.entries(container)) {
      if (!targets.includes(normalizeCampaignKey(key))) continue;
      const extracted = textFromAny(value);
      if (extracted) return extracted;
    }
  }

  return '';
};

const getCampaignTextValue = (
  campaign: CampaignMetadata | undefined,
  directKeys: string[],
  customFieldNames: string[] = []
): string => {
  if (!campaign) return '';
  const rawCampaign = campaign as CampaignMetadata & Record<string, any>;

  for (const key of directKeys) {
    const value = textFromAny(rawCampaign[key]);
    if (value) return value;
  }

  return getCampaignCustomField(campaign, customFieldNames);
};

const formatLabel = (value: unknown) => cleanText(value).replace(/_/g, ' ');

const getPersonLvl2Icp = (data: MappedLeadData) => {
  const icp = data.personLvl2?.icp;
  if (!icp) return null;
  if (typeof icp === 'object') return icp as Record<string, unknown>;
  return parseMaybeJson(icp) as Record<string, unknown> | null;
};


const getLatestInteraction = (data: MappedLeadData) => {
  const latest = [...(data.interactions || [])]
    .filter((interaction) => interaction?.timestamp)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

  if (!latest) return '';

  const date = new Date(latest.timestamp);
  const when = Number.isNaN(date.getTime())
    ? 'Recently'
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });

  return `${when}: ${truncateWords(latest.content || latest.meta?.subject || latest.type, 14)}`;
};

const getProfessionalSnapshot = (data: MappedLeadData) => {
  const personResearch = getResearchSummarySectionsForTab(data, 'person')[0]?.content;
  return firstPresent(personResearch, data.linkedinSummary, data.generatorSummary, data.leadExecutiveOverview);
};

const getRecentSignal = (data: MappedLeadData) => {
  const signalResearch = getResearchSummarySectionsForTab(data, 'signals')[0]?.content;
  const recent = data.recentActivities?.[0];
  const recentSummary = firstPresent(
    recent?.analysis?.actionable_insight,
    recent?.analysis?.strategic_value,
    recent?.analysis?.summary,
    recent?.analysis?.headline,
    recent?.data?.text,
    recent?.data?.activityDescription
  );

  return firstPresent(signalResearch, recentSummary);
};

const getNetworkFacts = (data: MappedLeadData): AccountFactModel[] => {
  const network = data.rawProfileIntel?.network_intelligence || {};
  const internalNetwork = network.internal_network || {};
  const summary = internalNetwork.network_summary || {};
  const buyingCommittee = network.relationship_mapping?.buying_committee_estimate || {};
  const internalConnections = internalNetwork.leadership_connections || [];
  const externalConnections = network.external_network?.industry_connections || [];
  const members = buyingCommittee.identified_members || [];
  const missingRoles = buyingCommittee.missing_roles || [];

  return ([
    summary.decision_making_style ? { label: 'Decision style', value: formatLabel(summary.decision_making_style) } : null,
    summary.org_structure_insight ? { label: 'Org structure', value: formatLabel(summary.org_structure_insight) } : null,
    summary.total_colleagues_identified ? { label: 'Known network', value: `${summary.total_colleagues_identified} colleagues identified` } : null,
    members.length ? { label: 'Buying committee', value: members.slice(0, 3).join(', ') } : null,
    missingRoles.length ? { label: 'Missing roles', value: missingRoles.slice(0, 3).join(', ') } : null,
    internalConnections.length ? { label: 'Internal connection', value: `${internalConnections[0].name}${internalConnections[0].title ? `, ${internalConnections[0].title}` : ''}` } : null,
    externalConnections.length ? { label: 'External connection', value: `${externalConnections[0].name}${externalConnections[0].company ? ` at ${externalConnections[0].company}` : ''}` } : null,
  ] as Array<AccountFactModel | null>)
    .filter((fact): fact is AccountFactModel => Boolean(fact?.value))
    .slice(0, 5);
};

const getCampaignSummary = (campaign?: CampaignMetadata) => firstPresent(
  textFromAny(campaign?.summary),
  getCampaignTextValue(campaign, ['campaign_summary'], ['Campaign Summary', 'Campaign Context Text'])
);

const formatOutreachChannels = (campaign?: CampaignMetadata) => {
  const rawCampaign = campaign as (CampaignMetadata & Record<string, any>) | undefined;
  const channels = rawCampaign?.outreach_channel ?? rawCampaign?.outreach_channels;

  if (!Array.isArray(channels)) {
    return firstPresent(
      textFromAny(channels),
      getCampaignCustomField(campaign, ['Outreach Channel'])
    );
  }

  return unique(channels.map((channel) => firstPresent(channel?.value, channel?.id)), 4).join(', ');
};

const getMissingCampaignItems = (resolved: Record<string, unknown>) => {
  const fields: Array<[string, unknown]> = [
    ['objective', resolved.objective],
    ['target niche', resolved.niche],
    ['pain points', resolved.pain],
    ['value proposition', resolved.value],
    ['success metrics', resolved.proof],
    ['outreach channel', resolved.outreachChannels],
  ];

  return fields
    .filter(([, value]) => !firstPresent(value))
    .map(([label]) => label);
};

const buildBriefing = (data: MappedLeadData, campaign?: CampaignMetadata): ISRBriefingViewModel => {
  const profileIntel = data.rawProfileIntel || {};
  const socialIntel = profileIntel.social_intelligence || {};
  const icebreakers = socialIntel.icebreaker_intelligence || {};
  const temporal = profileIntel.temporal_intelligence || {};
  const framework = profileIntel.engagement_framework || {};
  const empathy = profileIntel.empathy_intelligence || {};
  const psych = profileIntel.psychological_profile || {};
  const buyer = psych.buyer_archetype || profileIntel.buyer_archetype || {};
  const biases = profileIntel.cognitive_bias_profile || psych.cognitive_bias_profile || {};
  const cultural = profileIntel.cultural_intelligence || {};
  const generation = profileIntel.generational_profile || {};
  const strategy = parseMaybeJson(data.personLvl2PersonalizationStrategy) || {};
  const icpData = getPersonLvl2Icp(data) || {};

  const campaignSummary = getCampaignSummary(campaign);
  const campaignObjective = firstPresent(
    getCampaignTextValue(campaign, ['primary_campaign_objective', 'campaign_goal', 'goal'], ['Primary Campaign Objective']),
    getCampaignTextValue(campaign, ['desires', 'campaign_desires'], ['Campaign Desires']),
    campaignSummary,
    'Campaign objective not configured'
  );
  const campaignNiche = firstPresent(
    getCampaignTextValue(campaign, ['target_audience_niche', 'target_audience _niche'], ['Target Audience Niche (AI)', 'Targeted Companies']),
    getCampaignTextValue(campaign, ['buyer_personas'], ['Buyer Personas (AI)']),
    data.targetAudience
  );
  const campaignPain = firstPresent(
    getCampaignTextValue(campaign, ['target_audience_pain_points'], ['Target Audience Pain Points (AI)']),
    icpData.top_pain
  );
  const campaignValue = firstPresent(
    getCampaignTextValue(campaign, ['core_value_proposition', 'usp', 'unique_sales_point', 'unique_selling_point', 'unique_value_proposition', 'value_proposition'], ['Core Value Proposition (AI)']),
    icpData.value_hook
  );
  const campaignProof = firstPresent(
    getCampaignTextValue(campaign, ['success_metrics'], ['Success Metrics (AI)']),
    icpData.proof,
    getCampaignTextValue(campaign, ['guarantee', 'offer_guarantee'])
  );
  const outreachChannels = formatOutreachChannels(campaign);
  // Base the "Campaign context missing" warning on what the briefing can actually
  // display (campaign field OR lead-ICP fallback), not on the raw campaign object —
  // otherwise fields resolved from the lead's ICP data are falsely flagged as missing.
  const campaignObjectiveRaw = firstPresent(
    getCampaignTextValue(campaign, ['primary_campaign_objective', 'campaign_goal', 'goal'], ['Primary Campaign Objective']),
    getCampaignTextValue(campaign, ['desires', 'campaign_desires'], ['Campaign Desires']),
    campaignSummary
  );
  const missingCampaignItems = getMissingCampaignItems({
    objective: campaignObjectiveRaw,
    niche: campaignNiche,
    pain: campaignPain,
    value: campaignValue,
    proof: campaignProof,
    outreachChannels,
  });

  const bestTime = temporal?.optimal_send_times?.[0]
    ? `${temporal.optimal_send_times[0].day || ''} ${temporal.optimal_send_times[0].time || ''}`.trim()
    : firstPresent(getCampaignTextValue(campaign, ['best_outreach_timing'], ['Best Outreach Timing (AI)']), temporal?.local_business_hours, data.rawOriginalData?.best_outreach_timing);
  const employeeRange = firstPresent(data.size_range, data.companyEmployees, data.companySize, data.nr_employees);
  const revenue = formatRevenue(data.revenue || data.revenueRange);
  // Preferred language is LEAD-level first (the person's own language from
  // lvl1/lvl2 enrichment), then the lead's ICP-detected language, and only as a
  // last resort the campaign's default. Putting the campaign first made every
  // lead show the campaign's language regardless of the person's actual language.
  const language = firstPresent(
    data.language,
    data.languageCode,
    profileIntel.communication_preferences?.primary_language,
    getCampaignTextValue(campaign, ['preferred_language'], ['Preferred Language']),
  );
  const companyIndustry = firstPresent(data.companyIndustry, data.industry, data.cnae_text ? String(data.cnae_text).split(',')[0].trim() : '');
  const companyStage = firstPresent(data.companyStage, data.careerStage);

  const tier2Hooks = Array.isArray(icebreakers.tier_2_hooks) ? icebreakers.tier_2_hooks : [];
  const tier3Hooks = Array.isArray(icebreakers.tier_3_hooks) ? icebreakers.tier_3_hooks : [];
  const firstHook = [...tier2Hooks, ...tier3Hooks][0] || {};
  const conversationStarters = asList(icebreakers.conversation_starters);
  const languageStyles = asList(strategy.language_style);
  const avoidLanguage = asList(strategy.avoid_language);
  const avoidTopics = asList(icebreakers.avoid_topics);
  const culturalRedFlags = asList(cultural.cultural_red_flags);
  const negativeTriggers = asList(generation.generational_triggers?.negative_triggers);
  const latestSignal = getRecentSignal(data);
  const networkFacts = getNetworkFacts(data);
  const professionalSnapshot = getProfessionalSnapshot(data);
  const whoTheyAre = firstPresent(data.personLvl2Overview, data.leadExecutiveOverview, data.linkedinSummary);
  const accountSummary = firstPresent(
    data.companyExecutiveOverview,
    data.companyGeneratorSummary,
    data.business_database_summary,
    textFromAny(data.rawCompanySummary),
    textFromAny(data.companySummary)
  );
  const fitSummary = firstPresent(
    icpData.value_hook,
    data.personLvl2Overview,
    data.leadExecutiveOverview,
    professionalSnapshot,
    campaignNiche ? `${data.leadRole || 'This lead'} at ${data.companyName || 'the account'} fits the campaign target: ${campaignNiche}.` : ''
  );
  const whyNow = firstPresent(
    firstHook.reference,
    latestSignal,
    temporal?.optimal_send_times?.[0]?.rationale,
    framework.empathy_approach,
    getCampaignTextValue(campaign, ['best_outreach_timing'], ['Best Outreach Timing (AI)'])
  );
  const openingAngle = firstPresent(
    strategy.opening_approach,
    firstHook.opening_line,
    conversationStarters[0],
    campaignValue && campaignPain ? `Open by connecting ${campaignValue} to the account pain: ${campaignPain}.` : '',
    campaignValue ? `Open with the campaign value proposition: ${campaignValue}.` : '',
    framework.rapport_building
  );
  const likelyObjections = unique([
    strategy.objection_preemption,
    framework.objection_handling,
    biases.predicted_objections?.[0]?.objection,
    biases.predicted_objections?.[0]?.counter_strategy,
  ], 4);
  const avoid = unique([
    ...avoidLanguage,
    ...avoidTopics,
    ...culturalRedFlags,
    ...negativeTriggers,
  ], 5);
  const guidanceBullets = unique([
    languageStyles[0],
    languageStyles[1],
    framework.trust_building,
    buyer.buying_motivation ? `Buying motivation: ${formatLabel(buyer.buying_motivation)}.` : '',
    buyer.proof_required ? `Proof needed: ${formatLabel(buyer.proof_required)}.` : '',
    empathy.empathy_hooks?.[0]?.empathy_statement,
    textFromAny(campaign?.sales_frameworks) ? `Framework: ${formatLabel(textFromAny(campaign?.sales_frameworks))}.` : '',
    language ? `Preferred language: ${language}.` : '',
  ], 5);
  const fitBullets = unique([
    campaignNiche ? `Target niche: ${campaignNiche}.` : '',
    campaignPain ? `Pain to validate: ${campaignPain}.` : '',
    campaignValue ? `Value proposition: ${campaignValue}.` : '',
    campaignProof ? `Success metric: ${campaignProof}.` : '',
    outreachChannels ? `Primary channel: ${outreachChannels}.` : '',
  ], 5);
  const accountFacts = ([
    companyIndustry ? { label: 'Industry', value: companyIndustry } : null,
    employeeRange ? { label: 'Team size', value: employeeRange } : null,
    revenue ? { label: 'Revenue', value: revenue } : null,
    companyStage ? { label: 'Stage', value: companyStage } : null,
    ...networkFacts,
  ] as Array<AccountFactModel | null>)
    .filter((fact): fact is AccountFactModel => Boolean(fact?.value));
  const riskRows: RiskRowModel[] = [
    missingCampaignItems.length ? {
      label: 'Campaign context missing',
      value: `Missing ${missingCampaignItems.slice(0, 4).join(', ')}${missingCampaignItems.length > 4 ? ', and more' : ''}. Continue with lead intelligence, but treat strategy as incomplete.`,
      icon: 'solar:info-circle-bold',
      tone: 'amber',
    } : null,
    likelyObjections.length ? {
      label: 'Likely objection',
      value: likelyObjections.join(' '),
      icon: 'solar:shield-warning-bold',
      tone: 'rose',
    } : null,
    avoid.length ? {
      label: 'Avoid',
      value: avoid.join(' '),
      icon: 'solar:forbidden-circle-bold',
      tone: 'rose',
    } : null,
  ].filter(Boolean) as RiskRowModel[];

  return {
    context: {
      timezone: firstPresent(temporal.timezone, temporal.local_business_hours),
      bestTime: bestTime || undefined,
      latestActivity: getLatestInteraction(data),
      meetingUrl: firstPresent(getCampaignTextValue(campaign, ['meeting_url', 'campaign_meeting_url'], ['Campaign Meeting URL'])) || undefined,
    },
    angles: ([
      ['Top pain', cleanText(icpData.top_pain), 'pain'],
      ['Trigger', cleanText(icpData.trigger), 'trigger'],
      ['Value prop', cleanText(icpData.value_hook), 'value'],
      ['Proof', cleanText(icpData.proof), 'proof'],
    ] as Array<[string, string, 'pain' | 'trigger' | 'value' | 'proof']>)
      .filter(([, body]) => body)
      .map(([label, body, tone]) => ({ label, body, tone })),
    campaign: {
      name: firstPresent(getCampaignTextValue(campaign, ['name', 'campaign_name']), data.campaignName, 'Campaign'),
      objective: campaignObjective,
      summary: campaignSummary,
      niche: campaignNiche,
      pain: campaignPain,
      value: campaignValue,
      proof: campaignProof,
      outreachChannels,
      timing: firstPresent(
        bestTime,
        getCampaignTextValue(campaign, ['best_outreach_timing'], ['Best Outreach Timing (AI)']),
      ),
      missing: missingCampaignItems,
    },
    leadFit: {
      title: 'Lead Fit',
      eyebrow: 'Campaign fit and sales mission',
      icon: 'solar:target-bold',
      body: fitSummary || 'Lead fit is based on the available profile, ICP, and campaign context.',
      bullets: fitBullets,
    },
    whyNow: {
      title: 'Why Now',
      eyebrow: 'Timing signal',
      icon: 'solar:bolt-circle-bold',
      body: whyNow || 'No strong timing signal is available yet. Keep the outreach anchored to the campaign objective and account context.',
      bullets: unique([
        latestSignal ? `Signal: ${latestSignal}` : '',
        bestTime ? `Best window: ${bestTime}.` : '',
        getCampaignTextValue(campaign, ['best_outreach_timing'], ['Best Outreach Timing (AI)']) ? `Campaign timing: ${getCampaignTextValue(campaign, ['best_outreach_timing'], ['Best Outreach Timing (AI)'])}.` : '',
      ], 3),
    },
    accountContext: {
      title: 'Account Context',
      eyebrow: 'Supporting context',
      icon: 'solar:buildings-3-bold',
      body: accountSummary || professionalSnapshot || whoTheyAre || 'No extended account context is available yet.',
      facts: accountFacts,
    },
    openingAngle: {
      title: 'Opening Angle',
      eyebrow: 'First useful move',
      icon: 'solar:chat-round-dots-bold',
      body: openingAngle || 'Start with the most relevant business context, then ask one clear discovery question.',
      bullets: unique([
        campaignPain ? `Question to test: How are you currently handling ${campaignPain}?` : '',
        campaignValue ? `Bridge to value: ${campaignValue}.` : '',
        campaignProof ? `Proof to mention only if asked: ${campaignProof}.` : '',
      ], 3),
    },
    conversationGuidance: {
      title: 'Conversation Guidance',
      eyebrow: 'Tone, proof, and buying path',
      icon: 'solar:clipboard-list-bold',
      body: firstPresent(
        data.communicationStyle,
        strategy.communication_style,
        framework.empathy_approach,
        'Keep the conversation direct, specific, and anchored to evidence.'
      ),
      bullets: guidanceBullets.length ? guidanceBullets : ['Lead with business context.', 'Use proof instead of hype.', 'Ask for one concrete next step.'],
    },
    risks: riskRows.length ? riskRows : [{
      label: 'No major avoid signal',
      value: 'No explicit objections or avoid topics were found. Keep claims specific and evidence-based.',
      icon: 'solar:shield-check-bold',
      tone: 'slate',
    }],
  };
};


const BULLET_PREVIEW_LIMIT = 5;

const needsTextExpansion = (value: string, maxWords = 34, maxChars = 220) => {
  const cleaned = cleanText(value);
  if (!cleaned) return false;
  return cleaned.length > maxChars || cleaned.split(' ').filter(Boolean).length > maxWords;
};

const needsBulletExpansion = (bullets?: string[]) => (
  Boolean(bullets?.length && (
    bullets.length > BULLET_PREVIEW_LIMIT ||
    bullets.some((bullet) => needsTextExpansion(bullet, 16, 120))
  ))
);

const ExpandToggle: React.FC<{
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}> = ({ expanded, onToggle, className = '' }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={expanded}
    className={`inline-flex items-center gap-1 rounded-md px-0.5 py-1 text-[11px] font-black uppercase tracking-[0.11em] text-white/70 transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60 ${className}`}
  >
    {expanded ? 'Show less' : 'Read more'}
    <Icon
      icon="solar:alt-arrow-down-linear"
      className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
    />
  </button>
);

/** Campaign reference tab — everything the lead inherits from its campaign,
 *  in one flat place. Lives on its own tab so it stops competing with
 *  call-critical briefing content. */
const CampaignContextTab: React.FC<{ briefing: ISRBriefingViewModel }> = ({ briefing }) => {
  const c = briefing.campaign;
  const rows: Array<[string, string]> = [
    ['Objective', c.objective],
    ['Summary', c.summary],
    ['Target niche', c.niche],
    ['Pain to solve', c.pain],
    ['Value proposition', c.value],
    ['Success metrics', c.proof],
    ['Outreach channels', c.outreachChannels],
    ['Best timing', c.timing],
  ];
  const presentRows = rows.filter(([, value]) => firstPresent(value));

  return (
    <SimpleTabContent>
      <div className="pb-8">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-[15px] font-medium text-white">{c.name}</h2>
          {briefing.context.meetingUrl && (
            <a
              href={briefing.context.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-[12px] text-white/75 transition hover:border-white/35 hover:text-white"
            >
              <Icon icon="solar:clapperboard-open-play-bold" className="h-3.5 w-3.5" />
              Meeting link
            </a>
          )}
        </div>

        <dl className="mt-4 divide-y divide-white/[0.06]">
          {presentRows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[150px_minmax(0,1fr)] gap-4 py-3">
              <dt className="text-[12px] uppercase tracking-[0.1em] text-white/40">{label}</dt>
              <dd className="break-words text-[13px] leading-6 text-white/80">{value}</dd>
            </div>
          ))}
        </dl>

        {!presentRows.length && (
          <p className="mt-4 rounded-xl border border-dashed border-white/10 px-5 py-8 text-center text-[13px] text-white/40">
            No campaign context is configured for this campaign.
          </p>
        )}

        {c.missing.length > 0 && (
          <div className={`mt-5 rounded-xl border px-3.5 py-3 ${RISK_TONES.amber}`}>
            <div className="flex gap-3">
              <Icon icon="solar:info-circle-bold" className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.13em]">Campaign context missing</p>
                <p className="mt-1 text-[13px] leading-5 text-[#D6DDEA]">
                  Missing {c.missing.slice(0, 4).join(', ')}{c.missing.length > 4 ? ', and more' : ''}. Continue with lead intelligence, but treat strategy as incomplete.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </SimpleTabContent>
  );
};

const QuietInsightSection: React.FC<QuietInsightModel & { className?: string; flush?: boolean }> = ({ title, eyebrow, icon, body, bullets, className = '', flush = false }) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = needsTextExpansion(body) || needsBulletExpansion(bullets);
  const visibleBullets = expanded ? bullets : bullets?.slice(0, BULLET_PREVIEW_LIMIT);

  return (
    <section className={`${flush ? '' : 'py-4 first:pt-0'} ${className}`}>
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">{title}</h3>
        <p className="truncate text-[11px] text-white/35">{eyebrow}</p>
      </div>

      <p className={`${expanded ? '' : 'line-clamp-3'} max-w-[80ch] text-[13px] leading-6 text-white/75`} title={body}>
        {body}
      </p>

      {visibleBullets && visibleBullets.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {visibleBullets.map((bullet, index) => (
            <li key={`${title}-${index}`} className="flex min-w-0 gap-2 text-[13px] leading-6 text-white/65">
              <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-white/35" />
              <span className={`${expanded ? 'break-words' : 'line-clamp-1'} min-w-0`} title={bullet}>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {canExpand && (
        <ExpandToggle expanded={expanded} onToggle={() => setExpanded((value) => !value)} className="mt-2" />
      )}
    </section>
  );
};

const ACCOUNT_FACT_PREVIEW_LIMIT = 5;

/** Account narrative + normalized account attributes. Facts use a definition
 * list instead of generic bullets so an ISR can scan the account shape first. */
const AccountContextSection: React.FC<AccountContextModel & { className?: string }> = ({
  title,
  eyebrow,
  icon,
  body,
  facts,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = needsTextExpansion(body, 48, 320) || facts.length > ACCOUNT_FACT_PREVIEW_LIMIT;
  const visibleFacts = expanded ? facts : facts.slice(0, ACCOUNT_FACT_PREVIEW_LIMIT);

  return (
    <section className={className}>
      <div className="mb-3 flex items-center gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-300/[0.08] text-cyan-200/75 ring-1 ring-inset ring-cyan-200/10">
          <Icon icon={icon} className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/75">{title}</h3>
          <p className="truncate text-[11px] text-white/35">{eyebrow}</p>
        </div>
      </div>

      <p className={`${expanded ? '' : 'line-clamp-6'} break-words text-[13px] leading-6 text-white/75`} title={body}>
        {body}
      </p>

      {visibleFacts.length > 0 && (
        <dl className="mt-3 divide-y divide-white/[0.06] border-y border-white/[0.07]">
          {visibleFacts.map((fact) => (
            <div key={`${fact.label}-${fact.value}`} className="grid grid-cols-[minmax(5.5rem,0.42fr)_minmax(0,1fr)] gap-3 py-2">
              <dt className="text-[10px] font-semibold uppercase tracking-[0.11em] text-white/38">{fact.label}</dt>
              <dd className="min-w-0 break-words text-[13px] leading-5 text-white/78">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {canExpand && (
        <ExpandToggle expanded={expanded} onToggle={() => setExpanded((value) => !value)} className="mt-2" />
      )}
    </section>
  );
};

const RISK_TONES: Record<RiskRowModel['tone'], string> = {
  amber: 'border-amber-300/18 bg-amber-300/[0.045] text-amber-100',
  rose: 'border-rose-300/16 bg-rose-300/[0.04] text-rose-100',
  slate: 'border-white/[0.07] bg-white/[0.025] text-[#D6DDEA]',
};

const RiskWarningRow: React.FC<RiskRowModel> = ({ label, value, icon, tone }) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = needsTextExpansion(value, 24, 150);

  return (
    <div className={`rounded-xl border px-3.5 py-3 ${RISK_TONES[tone]}`}>
      <div className="flex gap-3">
        <Icon icon={icon} className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.13em]">{label}</p>
          <p className={`mt-1 ${expanded ? '' : 'line-clamp-3'} text-[13px] leading-5 text-[#D6DDEA]`} title={value}>{value}</p>
          {canExpand && (
            <ExpandToggle expanded={expanded} onToggle={() => setExpanded((current) => !current)} className="mt-2" />
          )}
        </div>
      </div>
    </div>
  );
};

const RisksSection: React.FC<{ rows: RiskRowModel[]; className?: string }> = ({ rows, className = '' }) => (
  <section className={`py-4 ${className}`}>
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">Risks / Avoid</h3>
      <p className="truncate text-[11px] text-white/35">Only the context that can damage trust</p>
    </div>
    {rows.map((row) => (
      <RiskWarningRow key={`${row.label}-${row.tone}`} {...row} />
    ))}
  </section>
);

/** Tone accent per ICP angle — subtle left border + label tint. */
const ANGLE_TONES: Record<'pain' | 'trigger' | 'value' | 'proof', { border: string; label: string }> = {
  pain: { border: 'border-rose-400/45', label: 'text-rose-200/80' },
  trigger: { border: 'border-amber-300/45', label: 'text-amber-200/80' },
  value: { border: 'border-cyan-300/45', label: 'text-cyan-200/80' },
  proof: { border: 'border-emerald-300/45', label: 'text-emerald-200/80' },
};

/** Lead-level ICP angles — pain / trigger / value / proof as flat rows with
 *  a tone accent. This is the most call-critical material, so it leads the
 *  Briefing. Hidden entirely when the lead has no ICP angles. */
const IcpAnglesSection: React.FC<{ angles: ISRBriefingViewModel['angles']; className?: string }> = ({ angles, className = '' }) => {
  if (!angles.length) return null;
  return (
    <section className={className}>
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">Angles</h3>
        <p className="truncate text-[11px] text-white/35">Pain, trigger, value, and proof for this lead</p>
      </div>
      <div className="space-y-2">
        {angles.map((angle) => {
          const tone = ANGLE_TONES[angle.tone];
          return (
            <div key={angle.label} className={`border-l-2 py-1 pl-3.5 ${tone.border}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${tone.label}`}>{angle.label}</p>
              <p className="mt-1 break-words text-[13px] leading-6 text-white/78">{angle.body}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ISRBriefingTab: React.FC<ISRBriefingTabProps> = ({ data, campaign }) => {
  const briefing = useMemo(() => buildBriefing(data, campaign), [data, campaign]);

  return (
    <SimpleTabContent>
      <div className="@container pb-8">
        <div className={`border-b border-white/[0.06] ${briefing.angles.length ? '@min-[680px]:grid @min-[680px]:grid-cols-[minmax(0,1.08fr)_minmax(16rem,0.92fr)]' : ''}`}>
          <IcpAnglesSection angles={briefing.angles} className="pb-4 @min-[680px]:pr-6" />
          <AccountContextSection
            {...briefing.accountContext}
            className={`pb-4 ${briefing.angles.length ? 'border-t border-white/[0.06] pt-4 @min-[680px]:border-l @min-[680px]:border-t-0 @min-[680px]:pl-6 @min-[680px]:pt-0' : ''}`}
          />
        </div>
        <div className="border-b border-white/[0.06] @min-[680px]:grid @min-[680px]:grid-cols-[minmax(0,1.08fr)_minmax(16rem,0.92fr)]">
          <QuietInsightSection {...briefing.openingAngle} flush className="py-4 @min-[680px]:pr-6" />
          <QuietInsightSection
            {...briefing.whyNow}
            flush
            className="border-t border-white/[0.06] py-4 @min-[680px]:border-l @min-[680px]:border-t-0 @min-[680px]:pl-6"
          />
        </div>
        <QuietInsightSection {...briefing.leadFit} />
      </div>
    </SimpleTabContent>
  );
};

/**
 * Guidance tab — how to run the conversation (tone, proof, buying path)
 * plus the risks/avoid list. Split from the Briefing so the call-critical
 * "what to say now" content stays scannable.
 */
export const ISRGuidanceTab: React.FC<ISRBriefingTabProps> = ({ data, campaign }) => {
  const briefing = useMemo(() => buildBriefing(data, campaign), [data, campaign]);

  return (
    <SimpleTabContent>
      <div className="divide-y divide-white/[0.06] pb-8">
        <QuietInsightSection {...briefing.conversationGuidance} />
        <RisksSection rows={briefing.risks} />
      </div>
    </SimpleTabContent>
  );
};

export { CampaignContextTab };
/** Public alias so the ISR workspace can build the campaign tab without
 *  duplicating campaign-context resolution. */
export { buildBriefing as buildBriefingPublic };
